<template>
    <div class="lnk-bigfile-upload">
        <uploader-unsupport></uploader-unsupport>
        <div class="trigger-box" v-if="!drag">
            <uploader-btn :attrs="{accept}"> <slot name="file-trigger"></slot> </uploader-btn>
            <uploader-btn :directory="true" :attrs="{accept}">
                <slot name="folder-trigger"></slot>
            </uploader-btn>
        </div>
        <div class="drag-box" v-else>
            <uploader-drop> <i class="el-icon-upload"></i> </uploader-drop>
        </div>
        <div class="files-box">
            <slot :files="files" :file-list="fileList" :started="started">
                <uploader-list></uploader-list>
            </slot>
        </div>
    </div>
</template>

<script>
/* eslint-disable prefer-rest-params */

import Uploader from './uploader/uploader';
import {camelcaseToHyphen} from '@/utils/linktools';
import UploaderBtn from './btn.vue';
import UploaderDrop from './drop.vue';
import UploaderUnsupport from './unsupport.vue';
import UploaderList from './list.vue';
import UploaderFile from './file.vue';

export default {
    name: 'lnk-bigfile-uploader',
    components: {
        UploaderBtn,
        UploaderDrop,
        UploaderUnsupport,
        UploaderList,
        UploaderFile
    },
    props: {
        drag: Boolean,   // 是否拖曳上传
        data: {
            type: Object,
            default: () => ({})
        },
        headId: String,  // 附件表对应头id
        moduleType: String, // 附件表记录上传文件模块类型
        options: {  // 文件上传配置对象，包括路径、请求头、分片大小等
            type: Object,
            default: () => ({})
        },
        accept: { //文件类型
            type: Object,
            default: () => '*'
        },
        autoStart: { // 是否自动开始上传
            type: Boolean,
            default: true
        },
        fileStatusText: {  // 文件上传状态提示的自定义对象
            type: Object,
            default() {
                return {
                    success: this.$t('LnkBigfileUploader.successText'),
                    error: this.$t('LnkBigfileUploader.errorText'),
                    uploading: this.$t('LnkBigfileUploader.uploadingText'),
                    paused: this.$t('LnkBigfileUploader.pausedText'),
                    waiting: this.$t('LnkBigfileUploader.waitingText')
                };
            }
        }
    },
    data() {
        return {
            started: false,
            files: [],
            fileList: [],
            eventMap: {},
            defaultOptions: {
                target: appCtx.ctxPath + `/action/${LinkVue.prefixUrl}/pageUploader/chunk`,
                testChunks: true,
                method: 'POST',
                simultaneousUploads: 1,
                chunkSize: 1 * 1024 * 1024,
                headers: {}
            }
        };
    },

    computed: {
        uploadOptions() {
            const options = this.defaultOptions;
            linkTools.coverPropsByTpl(options, this.options);
            return options;
        }
    },
    methods: {
        /**
         * @method：init
         * @createdBy:HuangJian
         * @date:2019/3/6
         * @params：
         * @description：初始化
         */
        init() {
            this.uploader = new Uploader(this.uploadOptions);
            this.uploader.fileStatusText = this.fileStatusText;
            this.eventMap = {
                filesAdded: this.filesAdded,
                fileRemoved: this.fileRemoved,
                uploadStart: this.uploadStarted,
                fileComplete: this.fileComplete,
                fileError: this.fileError,
                complete: this.complete
            };
            this.onEvents();
        },
        /**
         * @method：onEvents
         * @createdBy:HuangJian
         * @date:2019/3/6
         * @params：
         * @description：开启操作事件得监听
         */
        onEvents(){
            Object.keys(this.eventMap).
            forEach((key) => this.uploader.on(key, this.eventMap[key]));
        },
        /**
         * @method：
         * @createdBy:HuangJian
         * @date:2019/3/6
         * @params：
         * @description：关闭操作事件得监听
         */
        offEvents(){
            Object.keys(this.eventMap).
            forEach((key) => this.uploader.off(key, this.eventMap[key]));
        },
        /**
         * @method：filesAdded
         * @createdBy:HuangJian
         * @date:2019/3/6
         * @params：
         * @description：添加文件后触发
         */
        filesAdded(...args) {
            if (args[0].ignored || args[1].ignored) {
                return false;
            }
            this.files = this.uploader.files;
            this.fileList = this.uploader.fileList;
            const event = camelcaseToHyphen('filesAdded');
            this.$emit(event, ...args);
            if (this.autoStart) {
                this.uploader.upload();
            }
        },
        /**
         * @method：fileRemoved
         * @createdBy:HuangJian
         * @date:2019/3/6
         * @params：
         * @description：移除文件后触发
         */
        fileRemoved(...args) {
            this.files = this.uploader.files;
            this.fileList = this.uploader.fileList;
            const event = camelcaseToHyphen('fileRemoved');
            this.$emit(event, ...args);
        },
        /**
         * @method：uploadStarted
         * @createdBy:HuangJian
         * @date:2019/3/6
         * @params：
         * @description：上传开始后触发
         */
        uploadStarted() {
            this.started = true;
            this.$emit('start');
        },
        /**
         * @method：fileComplete
         * @createdBy:HuangJian
         * @date:2019/3/6
         * @params：
         * @description：单个文件上传完成后触发
         */
        async fileComplete(...args){
            const url = this.uploadOptions.target.replace(
                /\/\w*$/g,
                '/mergeFile'
            );
            const file = args[1];
            const params = linkTools.deepCopy(this.data);
            const data = {
                filename: file.name,
                identifier: file.uniqueIdentifier,
                totalSize: file.size,
                type: file.fileType
            };
            linkTools.coverPropsByTpl(params, data);
            this.headId && (params.headId = this.headId);
            this.moduleType && (params.moduleType = this.moduleType);
            try {
                const resp = await this.$http.post(url, params, true);
                const event = resp.success
                    ? camelcaseToHyphen('fileComplete')
                    : 'error';
                this.$emit(event, file, resp);
            } catch (e) {

                this.$nav.error(String(e));
            }
        },
        /**
         * @method：fileError
         * @createdBy:HuangJian
         * @date:2019/3/6
         * @params：
         * @description：单个文件上传异常时触发
         */
        fileError(...args){
            const event = camelcaseToHyphen('fileError');
            this.$emit(event, ...args);
        },
        /**
         * @method：complete
         * @createdBy:HuangJian
         * @date:2019/3/6
         * @params：
         * @description：所有文件上传操作结束后触发
         */
        complete(){
            const files = this.$getChildComponent('uploader-file', false);
            const isComplete = files.every((file) => file.isComplete);
            if(isComplete){
                this.$emit('complete', this.fileList);
            }
        }
    },
    created() {
        this.init();
    },
    beforeDestroy() {
        this.offEvents();
    }
};
</script>

<style lang="scss">
.lnk-bigfile-upload {
    position: relative;
    .files-box {
        margin-top: 10px;
    }
}
</style>
